import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import { useFootprintAndProduct } from '../../../../hooks/useFootprintAndProduct';
import EmissionValueWithSuffix from '../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { EmissionSymbol } from '../../../../shared/interfaces/IFootprint';
import styles from './ProductCarbonFootprintSection.module.css';

const ProductCarbonFootprintSection = (): JSX.Element => {
  const { t } = useTranslation();
  const { footprintDetails, isFootprintLoading: isLoading } = useFootprintAndProduct();

  return (
    <div
      className={classNames(
        'p-4 md:p-5 border-round-2xl border-right-none border-top-none border-bottom-none border-primary border-solid flex flex-column w-full',
        styles['container'],
      )}
    >
      {/* Title */}
      <h5 className='m-0 p-0 font-medium pb-3 text-primary'>
        {t('footprintDetailsPage.sections.productCarbonFootprintSection.title')}
      </h5>

      <div className='flex flex-row w-full'>
        {/* Excl. biogenic emissions & removals */}
        <div className='flex flex-column gap-2 flex-1'>
          <span className='font-medium'>
            {t(
              'footprintDetailsPage.sections.productCarbonFootprintSection.exclBiogenicEmissionsAndRemovals',
            )}
          </span>
          {isLoading ? (
            <Skeleton width='120px' height='20px' />
          ) : (
            <EmissionValueWithSuffix
              emissionValue={footprintDetails?.pcf?.pcf_excluding_biogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={footprintDetails?.pcf?.declared_unit}
              abbreviateUnit
              unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
              suffixTextClassName='text-black-alpha-60'
              numericalValueTextClassName='text-black text-2xl font-medium'
            />
          )}
        </div>

        <Divider layout='vertical' className='mx-3 md:mx-6' />

        {/* Incl. biogenic emissions & removals */}
        <div className='flex flex-column gap-2 flex-1'>
          <span className='font-medium'>
            {t(
              'footprintDetailsPage.sections.productCarbonFootprintSection.inclBiogenicEmissionsAndRemovals',
            )}
          </span>
          {isLoading ? (
            <Skeleton width='120px' height='20px' />
          ) : (
            <EmissionValueWithSuffix
              emissionValue={footprintDetails?.pcf?.pcf_including_biogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={footprintDetails?.pcf?.declared_unit}
              abbreviateUnit
              unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
              suffixTextClassName='text-black-alpha-60'
              numericalValueTextClassName='text-black text-2xl font-medium'
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCarbonFootprintSection;
